/**
 * probeHistory — saved Probe rehearsal interviews.
 *
 * Every finished interview is posted here by `useProbeCall` (fire and
 * forget, once per call) and read back by the page's History sheet. The
 * server forwards to the domain API, which stores them org-scoped; the
 * agents reach the same records through `governance_list_probe_interviews`,
 * so "ask in chat about my last rehearsal" works without any hand-off.
 *
 * Each turn keeps the web pages the examiner linked under it during the
 * call, so the history shows them where the call did; the interview's
 * "Sources" list is derived from those (`interviewSources`), never stored
 * twice.
 *
 * @module state/probeHistory
 */
import {
  PROBE_BRIEFING_PASSAGE_MAX_CHARS,
  PROBE_INTERVIEW_MAX_DURATION_MS,
  PROBE_INTERVIEW_MAX_LOOKUPS,
  PROBE_INTERVIEW_MAX_RECORDS,
  PROBE_INTERVIEW_MAX_TURNS,
  PROBE_LOOKUP_MAX_RECORDS,
  PROBE_LOOKUP_REASON_MAX_CHARS,
  PROBE_RECORD_FIELD_MAX_CHARS,
  PROBE_SOURCE_TITLE_MAX_CHARS,
  PROBE_SOURCE_URL_MAX_CHARS,
  PROBE_TURN_MAX_CHARS,
  PROBE_TURN_MAX_LOOKUPS,
  PROBE_TURN_MAX_SOURCES,
  ProbeLookupArguments,
  ProbeRecord,
  ProbeTurnLookup,
  ProbeTurnSource,
  type ProbeInterviewSave,
  type ProbeTurn,
} from "@amoreg/contracts";
import {
  practiceInterviewOf,
  type PracticeInterview,
  type ProbeInterviewReview,
} from "@amoreg/shared/probePractice";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";

import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";
import {
  finishedLookups,
  linkableSource,
  savedTurns,
  type ProbeConversationState,
  type ProbeFinishedLookup,
  type ProbeSavedTurn,
  type ProbeSource,
  type ProbeTranscriptEntry,
} from "./probeSession";

/** A saved interview as the lists show it: the practice picture's reading of it, and its length. */
export interface ProbeInterviewSummary extends PracticeInterview {
  readonly summary: string | null;
  readonly turnCount: number;
  /** How long the conversation ran; null on interviews saved before it was measured. */
  readonly durationMs: number | null;
}

export {
  debriefOf,
  reviewOf,
  type ProbeInterviewReview,
  type ProbeInterviewReviewMoment,
} from "@amoreg/shared/probePractice";

export interface ProbeInterviewTurn {
  /** Its position in the turns as saved, which a review's moment points at. */
  readonly position: number;
  readonly role: "user" | "assistant";
  readonly text: string;
  readonly sources: ReadonlyArray<ProbeSource>;
  readonly lookups: ReadonlyArray<ProbeTurnLookup>;
}

export type ProbeCallRecord = Pick<
  ProbeConversationState,
  "transcript" | "sources" | "tools" | "records"
>;

export interface ProbeInterviewDetail extends ProbeInterviewSummary {
  readonly turns: ReadonlyArray<ProbeInterviewTurn>;
}

/** The browser's keepalive body limit is 64 KiB; stay under it. */
export const KEEPALIVE_MAX_BYTES = 60_000;

const url = (params: Record<string, string>) =>
  resolvePrimaryEnvironmentHttpUrl("/api/amoreg/probe/history", params);

const str = (value: unknown): string => (typeof value === "string" ? value : "");

const toSummary = (row: unknown): ProbeInterviewSummary | null => {
  const interview = practiceInterviewOf(row);
  if (interview === null) return null;
  const record = row as Record<string, unknown>;
  const duration = record["durationMs"];
  return {
    ...interview,
    summary: typeof record["summary"] === "string" ? record["summary"] : null,
    turnCount: typeof record["turnCount"] === "number" ? record["turnCount"] : 0,
    durationMs:
      typeof duration === "number" && Number.isFinite(duration) && duration >= 0 ? duration : null,
  };
};

/** How many interviews the Probe area lists — the API's most. */
export const PROBE_INTERVIEW_LIST_LIMIT = 50;

/** Recent interviews, newest first; null when the list is unavailable. */
export const listProbeInterviews = async (
  organizationId: string,
  limit?: number,
): Promise<ReadonlyArray<ProbeInterviewSummary> | null> => {
  const params: Record<string, string> =
    limit === undefined ? { organizationId } : { organizationId, limit: String(limit) };
  const response = await fetch(url(params), { credentials: "include" }).catch(() => null);
  if (response === null || !response.ok) return null;
  const json = (await response.json().catch(() => null)) as { interviews?: unknown } | null;
  if (json === null || !Array.isArray(json.interviews)) return null;
  return json.interviews.flatMap((row) => toSummary(row) ?? []);
};

const decodeLookup = Schema.decodeUnknownOption(ProbeTurnLookup);
const decodeRecord = Schema.decodeUnknownOption(ProbeRecord);

const lookupOf = (value: unknown): ReadonlyArray<ProbeTurnLookup> => {
  if (typeof value !== "object" || value === null) return [];
  const records = (value as Record<string, unknown>)["records"];
  return Option.toArray(
    decodeLookup({
      ...value,
      records: Array.isArray(records)
        ? records.flatMap((record: unknown) => Option.toArray(decodeRecord(record)))
        : records,
    }),
  );
};

/**
 * A past interview's turns as the API sent them. Read defensively, like the
 * rest of this module: a turn without text is skipped, keeping the others'
 * saved positions, and a source the page could not draw as a link is
 * dropped — interviews saved before sources were kept simply have none.
 */
export const interviewTurnsOf = (value: unknown): ReadonlyArray<ProbeInterviewTurn> =>
  Array.isArray(value)
    ? value.flatMap((row: unknown, position) => {
        if (typeof row !== "object" || row === null) return [];
        const record = row as Record<string, unknown>;
        const text = str(record["text"]);
        if (text.length === 0) return [];
        const sources = Array.isArray(record["sources"])
          ? record["sources"].flatMap((source: unknown) => {
              if (typeof source !== "object" || source === null) return [];
              const page = source as Record<string, unknown>;
              return linkableSource(str(page["title"]), str(page["url"])) ?? [];
            })
          : [];
        const lookups = Array.isArray(record["lookups"]) ? record["lookups"].flatMap(lookupOf) : [];
        const role = record["role"] === "user" ? ("user" as const) : ("assistant" as const);
        return [{ position, role, text, sources, lookups }];
      })
    : [];

/** One interview read: found, not this organization's (or gone), or unreachable. */
export type ProbeInterviewRead =
  | { readonly kind: "found"; readonly interview: ProbeInterviewDetail }
  | { readonly kind: "missing" }
  | { readonly kind: "failed" };

/** One interview with its turns. */
export const readProbeInterview = async (
  organizationId: string,
  interviewId: string,
  signal?: AbortSignal,
): Promise<ProbeInterviewRead> => {
  const response = await fetch(url({ organizationId, interviewId }), {
    credentials: "include",
    ...(signal ? { signal } : {}),
  }).catch(() => null);
  if (response === null) return { kind: "failed" };
  // The API answers 404 for an interview that is not this organization's.
  if (response.status === 404 || response.status === 400) return { kind: "missing" };
  if (!response.ok) return { kind: "failed" };
  const interview = interviewDetailOf(await response.json().catch(() => null));
  return interview === null ? { kind: "failed" } : { kind: "found", interview };
};

/** One interview as the API sent it; null when it is not one. */
export const interviewDetailOf = (json: unknown): ProbeInterviewDetail | null => {
  const summary = toSummary(json);
  if (summary === null) return null;
  return { ...summary, turns: interviewTurnsOf((json as Record<string, unknown>)["turns"]) };
};

/**
 * Ask for an interview's review again, after its last run failed or was
 * lost. Answers the review as it now stands (pending when this asked for a
 * run, or whatever an earlier run left), or null when the ask failed.
 */
export const retryProbeReview = async (
  organizationId: string,
  interviewId: string,
): Promise<ProbeInterviewReview | null> => {
  const response = await fetch(
    resolvePrimaryEnvironmentHttpUrl("/api/amoreg/probe/history/review", {
      organizationId,
      interviewId,
    }),
    { method: "POST", credentials: "include" },
  ).catch(() => null);
  if (response === null || !response.ok) return null;
  return toSummary(await response.json().catch(() => null))?.review ?? null;
};

/**
 * Every page the examiner checked in one interview, once each, in the order
 * it first linked them — the "Sources" list under the summary. Derived from
 * the turns rather than stored a second time, and kept out of the summary
 * itself: the summary is what the examiner reads back next time, and an
 * address is nothing it should say aloud.
 */
export const interviewSources = (
  turns: ReadonlyArray<{ readonly sources?: ReadonlyArray<ProbeSource> }>,
): ReadonlyArray<ProbeSource> => {
  const seen = new Set<string>();
  return turns.flatMap((turn) =>
    (turn.sources ?? []).filter((source) => {
      if (seen.has(source.url)) return false;
      seen.add(source.url);
      return true;
    }),
  );
};

/**
 * The saved interview's name: the examiner's first question, cut to a
 * line — the one thing that distinguishes rehearsals in a list. Falls back
 * to the examiner's first line when it never asked one, and to a dated
 * label when it never spoke.
 */
export const probeInterviewTitle = (turns: ReadonlyArray<ProbeTurn>, today: string): string => {
  const spoken = turns.filter((turn) => turn.role === "assistant" && turn.text.trim().length > 0);
  const question = spoken
    .flatMap((turn) => turn.text.match(/[^.!?…]*\?/g) ?? [])
    .map((sentence) => sentence.trim())
    .find((sentence) => sentence.length > 1);
  const line = (question ?? spoken[0]?.text ?? "").replace(/\s+/g, " ").trim();
  if (line.length === 0) return `Probe interview, ${today}`;
  return line.length <= 80 ? line : `${line.slice(0, 79).trimEnd()}…`;
};

/** Postgres's jsonb refuses half a surrogate pair, so a cut never ends inside one. */
const cut = (value: string, max: number): string => {
  const kept = value.slice(0, max);
  return /[\uD800-\uDBFF]$/.test(kept) ? kept.slice(0, -1) : kept;
};

/**
 * A turn's sources inside the contract's limits: a page at most once, a
 * title clipped, an address too long to keep dropped (a cut address is a
 * broken link), and no more than the contract's handful.
 */
const sourcesForSave = (sources: ReadonlyArray<ProbeSource>): ReadonlyArray<ProbeSource> =>
  interviewSources([{ sources }])
    .filter((source) => source.url.length <= PROBE_SOURCE_URL_MAX_CHARS)
    .slice(0, PROBE_TURN_MAX_SOURCES)
    .map((source) => ({
      title: cut(source.title, PROBE_SOURCE_TITLE_MAX_CHARS),
      url: source.url,
    }));

const encoder = new TextEncoder();
const bytesOf = (value: unknown): number => encoder.encode(JSON.stringify(value)).byteLength;

export const fitForKeepalive = (
  body: ProbeInterviewSave,
  maxBytes: number = KEEPALIVE_MAX_BYTES,
): ProbeInterviewSave => {
  let size = bytesOf(body);
  if (size <= maxBytes) return body;
  const bare = body.turns.map(({ role, text }): ProbeTurn => ({ role, text }));
  if (bytesOf({ ...body, turns: bare }) > maxBytes) return body;
  const turns = [...body.turns];
  for (const key of ["lookups", "sources"] as const) {
    for (let index = 0; index < turns.length && size > maxBytes; index += 1) {
      const turn = turns[index]!;
      if (turn[key] === undefined) continue;
      const { [key]: _dropped, ...stripped } = turn;
      size -= bytesOf(turn) - bytesOf(stripped);
      turns[index] = stripped;
    }
  }
  return { ...body, turns };
};

const clip = (value: string): string => cut(value, PROBE_RECORD_FIELD_MAX_CHARS);
const clipOrNull = (value: string | null): string | null => (value === null ? null : clip(value));
const isAddress = Schema.is(ProbeTurnSource.fields.url);
const isRecord = Schema.is(ProbeRecord);
const isLookupArguments = Schema.is(ProbeLookupArguments);

const clipped = (record: ProbeRecord): ProbeRecord => {
  switch (record.kind) {
    case "finding":
      return {
        ...record,
        title: clip(record.title),
        severity: clipOrNull(record.severity),
        status: clip(record.status),
      };
    case "policy":
      return {
        ...record,
        title: clipOrNull(record.title),
        facts:
          record.facts === null
            ? null
            : {
                lifecycle: clipOrNull(record.facts.lifecycle),
                owner: clipOrNull(record.facts.owner),
                reviewDue: clipOrNull(record.facts.reviewDue),
              },
      };
    case "update":
      return {
        ...record,
        title: clip(record.title),
        date: clipOrNull(record.date),
        jurisdiction: clip(record.jurisdiction),
        url: record.url !== null && isAddress(record.url) ? record.url : null,
      };
    case "passage":
      return {
        ...record,
        title: clip(record.title),
        source: clipOrNull(record.source),
        text: cut(record.text, PROBE_BRIEFING_PASSAGE_MAX_CHARS),
      };
    case "rehearsal":
      return { ...record, title: clip(record.title), date: clip(record.date) };
    case "web":
      return { ...record, title: clip(record.title) };
  }
};

const recordForSave = (record: ProbeRecord): ReadonlyArray<ProbeRecord> => {
  const kept = clipped(record);
  return isRecord(kept) ? [kept] : [];
};

const argumentsForSave = (args: ProbeFinishedLookup["arguments"]): ProbeLookupArguments =>
  Object.fromEntries(
    Object.keys(ProbeLookupArguments.fields).flatMap((key) => {
      const raw = args[key];
      const value = typeof raw === "string" ? clip(raw.trim()) : raw;
      return value !== undefined && isLookupArguments({ [key]: value }) ? [[key, value]] : [];
    }),
  );

const lookupForSave = (lookup: ProbeFinishedLookup): ProbeTurnLookup => ({
  name: cut(lookup.name, 120),
  arguments: argumentsForSave(lookup.arguments),
  status: lookup.status,
  reason: lookup.reason === null ? null : cut(lookup.reason, PROBE_LOOKUP_REASON_MAX_CHARS),
  atMs: Math.min(Math.max(0, Math.round(lookup.atMs)), PROBE_INTERVIEW_MAX_DURATION_MS),
  records: lookup.records.flatMap(recordForSave).slice(0, PROBE_LOOKUP_MAX_RECORDS),
});

const lookupsForSave = (
  turns: ReadonlyArray<ReadonlyArray<ProbeFinishedLookup>>,
): ReadonlyArray<ReadonlyArray<ProbeTurnLookup>> => {
  let lookupsLeft = PROBE_INTERVIEW_MAX_LOOKUPS;
  let recordsLeft = PROBE_INTERVIEW_MAX_RECORDS;
  return turns.map((lookups) =>
    lookups
      .slice(0, PROBE_TURN_MAX_LOOKUPS)
      .map(lookupForSave)
      .filter((lookup) => {
        if (lookupsLeft === 0 || lookup.records.length > recordsLeft) return false;
        lookupsLeft -= 1;
        recordsLeft -= lookup.records.length;
        return true;
      }),
  );
};

const lastTurnsWithEarlierLookups = (
  turns: ReadonlyArray<ProbeSavedTurn>,
): ReadonlyArray<ProbeSavedTurn> => {
  const kept = turns.slice(-PROBE_INTERVIEW_MAX_TURNS);
  const carried = turns.slice(0, -PROBE_INTERVIEW_MAX_TURNS).flatMap((turn) => turn.lookups ?? []);
  const [first, ...rest] = kept;
  if (first === undefined || carried.length === 0) return kept;
  return [{ ...first, lookups: [...carried, ...(first.lookups ?? [])] }, ...rest];
};

/**
 * The body a finished interview is saved with, inside the contract's limits
 * — the server refuses the whole save otherwise, and a fire-and-forget save
 * would lose it silently. A long rehearsal keeps its tail, where the
 * debrief is; an overlong turn keeps its opening. Null when there is not
 * enough of a conversation to keep.
 */
export const interviewForSave = (
  call: ProbeCallRecord,
  today: string,
): ProbeInterviewSave | null => {
  const kept = lastTurnsWithEarlierLookups(
    savedTurns(call.transcript, call.sources, finishedLookups(call)),
  );
  const lookups = lookupsForSave(kept.map((turn) => turn.lookups ?? []));
  const turns = kept.map((turn, index): ProbeTurn => {
    const sources = sourcesForSave(turn.sources ?? []);
    const read = lookups[index] ?? [];
    return {
      role: turn.role,
      text: cut(turn.text, PROBE_TURN_MAX_CHARS),
      ...(sources.length === 0 ? {} : { sources }),
      ...(read.length === 0 ? {} : { lookups: read }),
    };
  });
  if (turns.length < 2) return null;
  const durationMs = conversationLengthMs(call.transcript);
  return {
    title: probeInterviewTitle(turns, today),
    turns,
    ...(durationMs === null ? {} : { durationMs }),
  };
};

/**
 * How long the conversation ran, on the session's own clock: from its first
 * word to its last. Null when the transcript carries no usable times.
 */
export const conversationLengthMs = (
  transcript: ReadonlyArray<Pick<ProbeTranscriptEntry, "startMs" | "endMs">>,
): number | null => {
  const times = transcript.flatMap((entry) =>
    [entry.startMs, entry.endMs].filter((time) => Number.isFinite(time) && time >= 0),
  );
  if (times.length === 0) return null;
  const length = Math.round(Math.max(...times) - Math.min(...times));
  return length > 0 && length <= PROBE_INTERVIEW_MAX_DURATION_MS ? length : null;
};

export type ProbeSaveOutcome =
  | { readonly status: "skipped" }
  | { readonly status: "failed" }
  | { readonly status: "saved"; readonly interview: ProbeInterviewSummary | null };

/**
 * Save a finished interview. Fire and forget by design: history must never
 * block or break the call it records. The server writes the summary the
 * examiner reads back next time; the browser only reports what was said,
 * and the pages the examiner linked while saying it. Answers what became of
 * the save, with the saved interview's entry when the server confirmed it,
 * so the page can open it.
 */
export const saveProbeInterview = async (
  organizationId: string,
  call: ProbeCallRecord,
  pageClosing: boolean,
): Promise<ProbeSaveOutcome> => {
  const body = interviewForSave(call, new Date().toISOString().slice(0, 10));
  if (body === null) return { status: "skipped" };
  const payload = JSON.stringify(pageClosing ? fitForKeepalive(body) : body);
  const response = await fetch(url({ organizationId }), {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: payload,
    // Browsers refuse a keepalive body over 64 KiB outright.
    keepalive: encoder.encode(payload).byteLength <= KEEPALIVE_MAX_BYTES,
  }).catch(() => null);
  if (response === null || !response.ok) return { status: "failed" };
  return { status: "saved", interview: toSummary(await response.json().catch(() => null)) };
};
